import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  linksControllerCreate,
  LinksControllerCreate$Params,
  linksControllerDelete,
  linksControllerDeleteImage,
  linksControllerFindAll,
  linksControllerFindById,
  linksControllerUpdate,
  LinksControllerUpdate$Params,
  linksControllerUploadImage,
  LinksControllerUploadImage$Params,
} from '@klubovna/api-client';
import { CreateLinkDto, Link, UpdateLinkDto } from '@klubovna/api-client';
import { PaginatedResponse, toPaginatedResponse } from '@klubovna/api-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface LinksQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: 'title' | 'sortOrder' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class LinksApiService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);

  findAll(params?: LinksQueryParams): Observable<PaginatedResponse<Link>> {
    const apiParams = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      isActive: params?.isActive,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    };
    return linksControllerFindAll(this.http, this.apiConfig.rootUrl, apiParams)
      .pipe(map(response => toPaginatedResponse<Link>(response.body)));
  }

  findOne(id: string): Observable<Link> {
    return linksControllerFindById(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as Link));
  }

  create(linkData: CreateLinkDto): Observable<Link> {
    const params: LinksControllerCreate$Params = {
      body: linkData,
    };
    return linksControllerCreate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Link));
  }

  update(id: string, linkData: UpdateLinkDto): Observable<Link> {
    const params: LinksControllerUpdate$Params = {
      id,
      body: linkData,
    };
    return linksControllerUpdate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Link));
  }

  delete(id: string): Observable<void> {
    return linksControllerDelete(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(() => undefined));
  }

  uploadImage(id: string, file: File): Observable<Link> {
    const params: LinksControllerUploadImage$Params = {
      id,
      body: { file },
    };
    return linksControllerUploadImage(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Link));
  }

  deleteImage(id: string): Observable<Link> {
    return linksControllerDeleteImage(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as Link));
  }
}
